import { Component, OnInit } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { UserApiCredentialsService, UserCredentialResponse, LoginHistory } from '../../core/services/user-api-credentials.service';

interface TokenClaim {
  key: string;
  label: string;
  value: string;
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit {

  private readonly defaultApiKeyName = 'My Api Key for URL Shortener';

  activeTab: 'profile' | 'developer' = 'profile';

  // Sidebar display fields
  displayName: string = '';
  displayEmail: string = '';
  firstInitial: string = '?';
  private username: string = '';

  // Profile tab – token claims
  claims: TokenClaim[] = [];

  // Profile tab – login history
  loginHistory: LoginHistory[] = [];
  loginHistoryLoading = true;
  loginHistoryError = '';

  // Developer tab – list of credential records from backend
  credentials: UserCredentialResponse[] = [];
  credentialLoading = true;
  credentialError = '';

  // API key modal
  apiKeyModalOpen = false;
  apiKeyMode: 'existing' | 'generate' = 'existing';
  keyName = '';
  keyNameTouched = false;  // shows inline validation once user attempts an action
  existingKey = '';
  generatedKey = '';      // raw key returned once by server
  generatingKey = false;
  generatedKeyCopied = false;
  modalStep: 'choose' | 'saving' = 'choose';
  modalError = '';

  constructor(
    private oauthService: OAuthService,
    private credentialService: UserApiCredentialsService
  ) {}

  ngOnInit(): void {
    this.loadClaims();
    this.loadCredentials();
    this.loadLoginHistory();
  }

  private loadClaims(): void {
    const raw = this.oauthService.getIdentityClaims();
    if (!raw) return;
    this.username     = raw['preferred_username'] || raw['email'] || '';
    this.displayName  = raw['name'] || raw['given_name'] || '';
    this.displayEmail = this.username;
    this.firstInitial = (raw['given_name'] || raw['name'] || '?')[0].toUpperCase();

    // EntraID app roles are in the 'roles' array claim; fall back to 'role' string claim
    const tokenRoles: string[] = raw['roles'] || (raw['role'] ? [raw['role']] : []);
    const tokenRole = tokenRoles.includes('admin') ? 'Admin' : 'Regular User';

    this.claims = [
      { key: 'name',               label: 'Full Name',        value: raw['name'] || '' },
      { key: 'preferred_username', label: 'Email / Username', value: this.username },
      { key: 'role',               label: 'Role',             value: tokenRole },
    ];
  }

  /** Update the displayed Role claim to reflect the highest role found across credential records. */
  private updateRoleFromCredentials(): void {
    const hasAdmin = this.credentials.some(c => c.role === 'admin');
    if (hasAdmin) {
      const roleEntry = this.claims.find(c => c.key === 'role');
      if (roleEntry) roleEntry.value = 'Admin';
    }
  }

  loadLoginHistory(): void {
    this.loginHistoryLoading = true;
    this.loginHistoryError   = '';
    this.credentialService.getLoginHistory(1, 10, this.username).subscribe({
      next: (res) => {
        this.loginHistory        = res.records;
        this.loginHistoryLoading = false;
      },
      error: () => {
        this.loginHistoryError   = 'Failed to load login history. Please try again.';
        this.loginHistoryLoading = false;
      }
    });
  }

  loadCredentials(): void {
    this.credentialLoading = true;
    this.credentialError   = '';
    this.credentialService.listMyCredentials(this.username).subscribe({
      next: (list) => {
        this.credentials       = list;
        this.credentialLoading = false;
        this.updateRoleFromCredentials();
      },
      error: () => {
        this.credentialError   = 'Failed to load API key information. Please try again.';
        this.credentialLoading = false;
      }
    });
  }

  setTab(tab: 'profile' | 'developer'): void {
    this.activeTab = tab;
  }

  openApiKeyModal(): void {
    this.keyName            = this.defaultApiKeyName;
    this.keyNameTouched     = false;
    this.apiKeyMode         = 'existing';
    this.existingKey        = '';
    this.generatedKey       = '';
    this.generatingKey      = false;
    this.generatedKeyCopied = false;
    this.modalStep          = 'choose';
    this.modalError         = '';
    this.apiKeyModalOpen    = true;
  }

  /** Calls POST /user-credentials/generate-apikey — server generates, stores hash, returns raw key once. */
  doGenerateKey(): void {
    this.keyNameTouched = true;
    if (!this.keyName.trim()) {
      return;
    }
    this.generatingKey = true;
    this.modalError    = '';
    this.credentialService.generateApiKey(this.username, this.keyName.trim()).subscribe({
      next: (res) => {
        this.generatedKey       = res.apiKey;
        this.generatedKeyCopied = false;
        this.generatingKey      = false;
        this.loadCredentials();
      },
      error: (err) => {
        this.generatingKey = false;
        const status = err?.status;
        if (status === 409) {
          this.modalError = 'A key with this name already exists (conflict). Try a different name.';
        } else {
          this.modalError = `Failed to generate key (HTTP ${status ?? 'unknown'}). Please try again.`;
        }
        console.error('generateApiKey error:', err);
      }
    });
  }

  copyGeneratedKey(): void {
    navigator.clipboard.writeText(this.generatedKey).then(() => {
      this.generatedKeyCopied = true;
      setTimeout(() => this.generatedKeyCopied = false, 2500);
    });
  }

  closeApiKeyModal(): void {
    if (this.modalStep === 'saving' || this.generatingKey) return;
    this.apiKeyModalOpen = false;
  }

  confirmApiKey(): void {
    this.keyNameTouched = true;
    if (this.apiKeyMode === 'generate') {
      if (!this.generatedKey) {
        this.modalError = 'Please click ↻ to generate a key first.';
        return;
      }
      this.apiKeyModalOpen = false;
      return;
    }
    // existing key mode
    if (!this.keyName.trim()) {
      return;
    }
    if (!this.existingKey.trim()) {
      this.modalError = 'Please paste your API key.';
      return;
    }
    this.modalStep  = 'saving';
    this.modalError = '';
    this.credentialService.registerApiKey(this.username, this.keyName.trim(), this.existingKey.trim()).subscribe({
      next: () => {
        this.apiKeyModalOpen = false;
        this.loadCredentials();
      },
      error: (err) => {
        this.modalStep  = 'choose';
        const status = err?.status;
        if (status === 409) {
          this.modalError = 'This API key is already registered (conflict). Try a different key.';
        } else if (status === 403) {
          this.modalError = 'You do not have permission to register this key.';
        } else {
          this.modalError = `Failed to save key (HTTP ${status ?? 'unknown'}). Please try again.`;
        }
        console.error('registerApiKey error:', err);
      }
    });
  }

  revokeCredential(cred: UserCredentialResponse): void {
    const label = cred.name ? `"${cred.name}"` : 'this credential';
    if (!confirm(`This will permanently revoke ${label} and its API key. Continue?`)) return;
    this.credentialService.deleteCredential(cred.uuid).subscribe({
      next: () => this.loadCredentials(),
      error: () => alert('Failed to revoke credential. Please try again.')
    });
  }
}
